// Native replacement for the "Observation" Google Form. Any teacher (@bee-happy.club, manager included).

import React from "react";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import {
  CONCENTRATION_OPTIONS,
  FAMILIARITY_OPTIONS,
  OBSERVATION_CATEGORIES,
  OLD_FORM_URLS,
  TEACHER_IDS,
  submitObservation,
  todayYmd,
  validateObservation,
  type ObservationInput,
} from "../../shared/nativeForms";
import {
  DateField, FormShell, PhotoNotice, SelectField, TextAreaField,
  childLabel, useAllowed, useChildOptions, useNativeForm, type ChildOption,
} from "./FormParts";

const empty = (): ObservationInput => ({
  childIds: [], teacherId: "", category: "", familiarity: "", concentration: "",
  observationNotes: "", nextSteps: "", observationDate: todayYmd(), photoFileUrl: "",
});

const ObservationFormPage: React.FC = () => {
  const allowed = useAllowed("teacher");
  const children = useChildOptions();
  const form = useNativeForm<ObservationInput>(empty, (values) => validateObservation(values, null), submitObservation);
  const { values, errors, set } = form;

  return (
    <FormShell
      title="New observation"
      allowed={allowed}
      deniedText="Observations can be added by Bee Happy staff only."
      oldFormUrl={OLD_FORM_URLS.observation}
      form={form}
      savedText="Observation saved. It shows in the lists the next time the portal loads."
      addAnotherLabel="Add another observation"
    >
      <Autocomplete<ChildOption, true>
        multiple
        disableCloseOnSelect
        options={children}
        getOptionLabel={childLabel}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={children.filter((child) => values.childIds.includes(child.id))}
        onChange={(_, selected) => set("childIds", selected.map((child) => child.id))}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}><Checkbox size="small" checked={selected} sx={{ mr: 1 }} />{childLabel(option)}</li>
        )}
        renderInput={(params) => (
          <TextField {...params} required label="Child" error={!!errors.childIds}
            helperText={errors.childIds || "Pick one child, or several for a group observation"} />
        )}
      />
      <SelectField label="Teacher" value={values.teacherId} options={TEACHER_IDS} onChange={(v) => set("teacherId", v)} error={errors.teacherId} />
      <SelectField label="Category" value={values.category} options={OBSERVATION_CATEGORIES} onChange={(v) => set("category", v)} error={errors.category} />
      <SelectField label="Familiarity" value={values.familiarity} options={FAMILIARITY_OPTIONS} onChange={(v) => set("familiarity", v)} error={errors.familiarity} />
      <SelectField label="Concentration" value={values.concentration} options={CONCENTRATION_OPTIONS} onChange={(v) => set("concentration", v)} error={errors.concentration} />
      <TextAreaField label="Observation notes" rows={4} value={values.observationNotes} onChange={(v) => set("observationNotes", v)} error={errors.observationNotes} />
      <TextAreaField label="Next steps" required={false} value={values.nextSteps} onChange={(v) => set("nextSteps", v)} error={errors.nextSteps} />
      <DateField label="Observation date" value={values.observationDate} onChange={(v) => set("observationDate", v)} error={errors.observationDate} />
      <PhotoNotice oldFormUrl={OLD_FORM_URLS.observation} />
    </FormShell>
  );
};

export default ObservationFormPage;
